const DIELINE_GUIDE_LAYER_NAME = "EASIL_DIELINE_GUIDE";

interface NamedElement {
  name?: string | null;
}

interface ElementLike extends NamedElement {
  type?: string;
  elementsOrder?: string[];
}

interface DesignDataLike {
  getElement?: (elementId: string) => ElementLike | undefined | null;
  elements?: Record<string, ElementLike | undefined | null>;
}

interface PageLike {
  elementsOrder?: string[];
}

const normaliseElementName = (name?: string | null) =>
  name?.trim().toUpperCase();

const getElement = (
  elementId: string,
  designData?: DesignDataLike | null
): ElementLike | undefined | null =>
  designData?.getElement?.(elementId) || designData?.elements?.[elementId];

export const isDielineGuideElement = (element?: NamedElement | null): boolean =>
  normaliseElementName(element?.name) === DIELINE_GUIDE_LAYER_NAME;

export const orderDielineGuidesAboveRegularElements = (
  elementIds: string[] = [],
  designData?: DesignDataLike | null
): string[] => {
  if (!designData) return elementIds;

  const regularElementIds: string[] = [];
  const guideElementIds: string[] = [];

  elementIds.forEach(elementId => {
    if (isDielineGuideElement(getElement(elementId, designData))) {
      guideElementIds.push(elementId);
    } else {
      regularElementIds.push(elementId);
    }
  });

  return [...regularElementIds, ...guideElementIds];
};

const orderChangedBetween = (
  next: string[],
  previous: string[] = []
): boolean => next.some((elementId, index) => elementId !== previous[index]);

/**
 * Returns `pages` with a single page's `elementsOrder` re-pinned so dieline
 * guides sit in the top band. Returns the same `pages` reference when the order
 * is unchanged so callers can cheaply short-circuit.
 */
export const normalisePageDielineGuideOrder = <T extends PageLike>(
  pages: Record<string, T>,
  pageId: string,
  designData?: DesignDataLike | null
): Record<string, T> => {
  const page = pages[pageId];
  if (!page?.elementsOrder) return pages;

  const orderedElements = orderDielineGuidesAboveRegularElements(
    page.elementsOrder,
    designData
  );

  if (!orderChangedBetween(orderedElements, page.elementsOrder)) return pages;

  return {
    ...pages,
    [pageId]: {
      ...page,
      elementsOrder: orderedElements
    }
  };
};

/**
 * Returns `pages` with every page in `pagesOrder` re-pinned so dieline guides
 * sit in the top band. Returns the same `pages` reference when nothing changed.
 */
export const normalisePagesDielineGuideOrder = <T extends PageLike>(
  pages: Record<string, T>,
  pagesOrder: string[] = [],
  designData?: DesignDataLike | null
): Record<string, T> =>
  pagesOrder.reduce(
    (pagesUpdated, pageId) =>
      normalisePageDielineGuideOrder(pagesUpdated, pageId, designData),
    pages
  );

const getBandElementIds = (
  elementIds: string[] = [],
  elementId: string,
  designData?: DesignDataLike | null
): string[] => {
  const selectedElementIsGuide = isDielineGuideElement(
    getElement(elementId, designData)
  );

  return elementIds.filter(
    currentElementId =>
      isDielineGuideElement(getElement(currentElementId, designData)) ===
      selectedElementIsGuide
  );
};

export const getDielineBandPositionActions = (
  elementIds: string[] = [],
  elementId: string,
  designData?: DesignDataLike | null
): string[] => {
  const bandElementIds = getBandElementIds(elementIds, elementId, designData);
  const elementIndexInBand = bandElementIds.indexOf(elementId);

  if (elementIndexInBand === -1) return [];

  const positionActions: string[] = [];

  if (elementIndexInBand + 1 !== bandElementIds.length) {
    positionActions.push("moveToTop", "moveUp");
  }

  if (elementIndexInBand !== 0) {
    positionActions.push("moveDown", "moveToBottom");
  }

  return positionActions;
};

export const getDielineBandMoveIndex = ({
  elementIds = [],
  elementId,
  moveAction,
  designData
}: {
  elementIds: string[];
  elementId: string;
  moveAction: string;
  designData?: DesignDataLike | null;
}): number => {
  const elementIndex = elementIds.indexOf(elementId);
  const bandElementIds = getBandElementIds(elementIds, elementId, designData);
  const elementIndexInBand = bandElementIds.indexOf(elementId);

  if (elementIndex === -1 || elementIndexInBand === -1) return elementIndex;

  const targetBandIndex = (() => {
    switch (moveAction) {
      case "moveToBottom":
        return 0;
      case "moveDown":
        return Math.max(elementIndexInBand - 1, 0);
      case "moveToTop":
        return bandElementIds.length - 1;
      case "moveUp":
        return Math.min(elementIndexInBand + 1, bandElementIds.length - 1);
      default:
        throw new Error("INVALID MOVE");
    }
  })();

  const targetElementId = bandElementIds[targetBandIndex];

  return targetElementId ? elementIds.indexOf(targetElementId) : elementIndex;
};

export const hasDielineGuideElement = (
  elementIds: string[] = [],
  designData?: DesignDataLike | null
): boolean => {
  if (!designData) return false;

  return elementIds.some(elementId => {
    const element = getElement(elementId, designData);

    if (isDielineGuideElement(element)) return true;

    return Boolean(
      element?.type === "group" &&
      element.elementsOrder?.some(groupElementId =>
        isDielineGuideElement(getElement(groupElementId, designData))
      )
    );
  });
};

export { DIELINE_GUIDE_LAYER_NAME };
